<template>
  <InputSkeleton :name="name"
                 :errors="errors"
                 class="TextInput"
                 :float-label="floatLabel"
                 :class="{
                   [`TextInput--${type}`]: !!type,
                   [`TextInput--${size}`]: !!size,
                   'TextInput--disabled': disabled,
                   'TextInput--readonly': readonly,
                   'TextInput--invalid': invalid || !!errors.length,
                   'TextInput--highlight': isHighlighted,
                   'TextInput--float-label': floatLabel,
                 }">
    <template v-if="$slots.default || label" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <div class="TextInput__input">
      <label v-if="$slots.prepend" :for="name">
        <slot name="prepend" />
      </label>
      <input ref="input"
             :id="name"
             :value="modelValue"
             :type="type"
             :name="name"
             :autocomplete="autocomplete"
             :autofocus="autofocus"
             :placeholder="placeholder"
             :disabled="disabled"
             :readonly="readonly"
             data-test-id="input"
             @input="onInput"
             @blur="onBlur"
             @keydown="onKeydown"
             @change="onChange"
      />
      <label v-if="$slots.append" :for="name">
        <slot name="append" />
      </label>
    </div>
  </InputSkeleton>
</template>

<script setup lang="ts">
  import Inputmask from 'inputmask';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { purifyPhone } from '@pn/utils';
  import { UiSize } from '../../../constants';
  import InputSkeleton from '../InputSkeleton.vue';
  import { InputErrorObject, TextInputMask, TextInputMode } from './types';

  const props = withDefaults(defineProps<{
    modelValue: string;
    type?: TextInputMode;
    name: string;
    label?: string;
    size?: UiSize;
    placeholder?: string;
    autocomplete?: 'on' | 'off';
    autofocus?: boolean;
    mask?: TextInputMask | string;
    invalid?: boolean;
    errors?: InputErrorObject[];
    disabled?: boolean;
    readonly?: boolean;
    highlight?: boolean;
    floatLabel?: boolean;
  }>(), {
    type: TextInputMode.TEXT,
    size: UiSize.MD,
    autocomplete: 'off',
    mask: null,
    errors: () => [],
    floatLabel: false,
    placeholder: '',
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: string): void;
    (e: 'change', modelValue: string): void;
    (e: 'blur', modelValue: string): void;
    (e: 'keydown', event: KeyboardEvent): void;
  }>();

  const input = ref<HTMLInputElement>();
  const inputMask = ref<Inputmask.Instance>();
  const isHighlighted = computed<boolean>(() => Boolean(props.highlight && !props.modelValue));

  const onInput = (event: Event) => {
    emit('update:model-value', (event.target as HTMLInputElement).value);
  };

  const onChange = (event: Event) => {
    emit('change', (event.target as HTMLInputElement).value);
  };

  const onBlur = (event: FocusEvent) => {
    emit('blur', (event.target as HTMLInputElement).value);
  };

  const onKeydown = (event: KeyboardEvent) => {
    emit('keydown', event);
  };

  const maskEnable = () => {
    if (props.type !== TextInputMode.TEXT || !props.mask) {
      return;
    }

    const options: Inputmask.Options = {
      showMaskOnFocus: true,
      showMaskOnHover: false,
    };

    switch (props.mask) {
      case TextInputMask.PHONE:
        options.mask = '+9 999 999 99 99';
        options.autoUnmask = true;
        options.onBeforeMask = (initialValue) => {
          // Начат ввод с кода государства
          if (initialValue.match(/^[7|8]$/)) {
            return '7';
          }

          // Вставка и буфера обмена
          if (initialValue.match(/^[7|8|+]/)) {
            return initialValue.replace(/^\d{1}/, '7');
          }

          // Начат ввод с кода оператора
          return `7${initialValue}`;
        };
        options.onUnMask = (maskedValue) => purifyPhone(maskedValue);
        break;

      case TextInputMask.INN:
        options.mask = '9999999999[99]';
        break;

      case TextInputMask.OGRN:
        options.mask = '9999999999999';
        break;

      case TextInputMask.OGRNIP:
        options.mask = '999999999999999';
        break;

      case TextInputMask.SNILS:
        options.mask = '999-999-999 99';
        break;

      case TextInputMask.CREDIT_CARD:
        options.mask = '9999 9999 9999 9999[ 99[9]]';
        break;

      case TextInputMask.ACT:
        options.mask = '999999999999999999999';
        options.placeholder = '';
        break;

      default:
        options.mask = props.mask;
        break;
    }

    inputMask.value = (new Inputmask(options)).mask(input.value);
  };

  const maskDisable = () => {
    if (!inputMask.value) {
      return;
    }

    inputMask.value.remove();
    inputMask.value = null;
  };

  watch(() => props.mask, () => {
    maskDisable();
    maskEnable();
  });

  onMounted(() => {
    maskEnable();
  });

  onBeforeUnmount(() => {
    maskDisable();
  });
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .TextInput {
    display: flex;
    flex-direction: column;
    width: 100%;

    &__input {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 100%;
    }

    &__label {
      display: block;
      margin-bottom: 10px;
      font-weight: 600;
    }

    label {
      white-space: nowrap;
    }

    input {
      @include transition($transition-function, $transition-duration, border-color);

      width: 100%;
      border: 1px solid $color-grey-75;
      border-radius: 4px;
      background-color: $color-white;
      line-height: 1;
      font-size: 14px;

      &[type='date']::-webkit-calendar-picker-indicator {
        width: 18px;
        height: 18px;
        padding: 0;
        background-image: url('../../../assets/sprite/calendar.svg');
        cursor: pointer;
      }
    }

    &--sm input {
      // ...
    }

    &--md input {
      height: 30px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--lg input {
      height: 40px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--xl input {
      height: 50px;
      padding-left: 15px;
      padding-right: 15px;
    }

    &--highlight input {
      background-color: #ffffd2;
    }

    &--disabled input {
      background-color: $color-grey-50;
    }

    &--disabled input {
      cursor: not-allowed;
    }

    input:focus {
      outline: none;
      border-color: $color-second;
    }

    input::placeholder {
      color: $color-grey-100;
    }

    &__toggle {
      cursor: pointer;
    }

    &--time &__toggle {
      position: absolute;
      right: 6px;
      top: 50%;
      width: 16px;
      height: 16px;
      transform: translate(0, -50%);
      padding: 0;
      background-color: transparent;
      color: $color-grey-75;
    }

    &--invalid input {
      border-color: $color-red-100;
    }

    &--invalid input::placeholder {
      color: $color-red-100;
    }

    &--invalid .svg-icon {
      color: $color-red-100;
    }

    &--float-label {
      @include float-label('TextInput');
    }
  }
</style>
